// LEA-505 — Ein-Klick-Nachkauf von Kontakt-Paketen.
//
// Die Bestätigungskarte: Paket wählen, den EXAKTEN Betrag sehen (von Stripe Tax
// für diesen Kunden gerechnet, inkl. USt. oder Reverse Charge), mit der beim
// ersten Checkout gespeicherten Karte bezahlen — ohne Umleitung. Verlangt die
// Bank 3DS, bestätigt der Kunde im selben Fenster (stripe.handleNextAction).
//
// Selbst-gated: der Server entscheidet, ob der Nachkauf angeboten wird (Schalter,
// Reveal-Modell, gespeicherte Karte, kein Erstkauf, eigene Session). Ohne
// `available` rendert die Karte nichts — Bestandskunden sehen keine Änderung.
// LEA-518: except a reveal customer without a refill (first purchase, no or a dead
// card), who gets ContactPackFirstPurchaseCard, the Checkout path, instead.
//
// Der Client rechnet nichts: Beträge kommen aus /quote, der Ausgang aus
// /charge, /complete bzw. /attempt. `confirmationId` gilt pro angezeigter Karte
// und wird bei jedem Retry derselben Karte wiederverwendet — so bucht ein
// Doppelklick nie zweimal ab. Ist der Ausgang eines Klicks UNBEKANNT (Antwort
// verloren, Tab neu geladen), bleibt die Karte bei dieser Id, bis der Server
// über /attempt gesagt hat, was passiert ist. Erst danach gibt es eine neue.

import { formatNumber, t, useLang } from '@cortex/i18n';
import type {
  ContactPackRefillQuote,
  ContactPackRefillResult,
  ContactPackRefillStatus,
  ContactPackRefillUnavailableReason,
  ContactPackSize,
} from '@cortex/schemas';
import { type Stripe as StripeJs, loadStripe } from '@stripe/stripe-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BillingError,
  chargeContactPackRefill,
  completeContactPackRefill,
  getContactPackRefill,
  quoteContactPackRefill,
  settleContactPackRefillAttempt,
} from '../../lib/billing';
import ContactPackFirstPurchaseCard from './ContactPackFirstPurchaseCard';

const stripeCache = new Map<string, Promise<StripeJs | null>>();
function loadStripeCached(pk: string): Promise<StripeJs | null> {
  let p = stripeCache.get(pk);
  if (!p) {
    // Ein fehlgeschlagener Ladeversuch (Netz, Adblocker) darf nicht für die
    // ganze Seitenlebensdauer hängen bleiben — der nächste Versuch lädt neu.
    p = loadStripe(pk).catch((err: unknown) => {
      stripeCache.delete(pk);
      throw err;
    });
    stripeCache.set(pk, p);
  }
  return p;
}

function newConfirmationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random()
    .toString(36)
    .slice(2)}`;
}

// Auf einer Zahlungsbestätigung immer mit Cents — formatCurrency lässt
// Nachkommastellen weg („117,8 €"), das ist für einen abzubuchenden Betrag falsch.
function money(cents: number, currency: string, lang: string): string {
  return new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

const DECLINE_CODES = new Set([
  'card_declined',
  'generic_decline',
  'insufficient_funds',
  'do_not_honor',
  'expired_card',
  'incorrect_cvc',
  'lost_card',
  'stolen_card',
  'card_not_supported',
  'transaction_not_allowed',
  'processing_error',
]);

function failureText(code: string): string {
  if (code.startsWith('authentication') || code === 'payment_intent_authentication_failure') {
    return t('refill.failedAuth');
  }
  if (DECLINE_CODES.has(code)) return t('refill.failedDeclined');
  return t('refill.failed');
}

// ─── Ein Klick mit unbekanntem Ausgang überlebt einen Reload ───────────────
type Attempt = {
  confirmationId: string;
  packSize: ContactPackSize;
  expectedAmountCents: number;
  at: number;
  /** Wem der Klick gehört — ein anderer User im selben Tab erbt ihn nicht. */
  uid: string | null;
};
const PENDING_KEY = 'ls.refill.pendingAttempt';
const PENDING_TTL_MS = 30 * 60 * 1000;

function currentUid(): string | null {
  const u = (window as unknown as { __cortexUser?: { uid?: string } | null }).__cortexUser;
  return typeof u?.uid === 'string' ? u.uid : null;
}

// LEA-518: refill reasons that still leave a reveal customer able to buy through
// Checkout. Left out: 'not_reveal_model' (not this model), 'billing_disabled'
// (Checkout is off too) and 'not_customer_session' (admin view, backplane or dev
// bypass; /checkout/* refuses operator sessions anyway).
const FIRST_PURCHASE_FALLBACK = new Set<ContactPackRefillUnavailableReason>([
  'disabled',
  'first_purchase',
  'no_saved_card',
  'card_unusable',
]);

// The server's own signal (/api/auth/me, LEA-518). 'disabled' is decided before
// the reveal check, so the reason alone doesn't tell a reveal customer apart.
function isRevealCustomer(): boolean {
  const u = (window as unknown as { __cortexUser?: { billingModel?: string } | null }).__cortexUser;
  return u?.billingModel === 'reveal';
}

/** Beim Logout (main.tsx __cortexLogout): der ungeklärte Klick gehört dem alten User. */
export function clearRefillPendingAttempt(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

function readPending(): Attempt | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const a = JSON.parse(raw) as Attempt;
    if (
      typeof a?.confirmationId !== 'string' ||
      ![100, 250, 500].includes(a.packSize) ||
      Date.now() - a.at > PENDING_TTL_MS ||
      a.uid !== currentUid()
    ) {
      sessionStorage.removeItem(PENDING_KEY);
      return null;
    }
    return a;
  } catch {
    return null;
  }
}

function writePending(a: Attempt | null): void {
  try {
    if (a) sessionStorage.setItem(PENDING_KEY, JSON.stringify(a));
    else sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* privater Modus o. ä. — dann eben ohne Reload-Schutz */
  }
}

function isQuote(x: unknown, packSize: ContactPackSize): x is ContactPackRefillQuote {
  const q = x as ContactPackRefillQuote | null;
  return (
    !!q &&
    q.packSize === packSize &&
    typeof q.amountDueCents === 'number' &&
    typeof q.taxCents === 'number' &&
    typeof q.currency === 'string'
  );
}

/** Kann der Ausgang eines /charge-Requests unbekannt sein? Ja bei Netzfehlern
 *  und 5xx — dann kann der Server gebucht haben, ohne dass die Antwort ankam. */
function outcomeUnknown(err: unknown): boolean {
  if (!(err instanceof BillingError)) return true;
  return err.status >= 500;
}

type Notice = { tone: 'success' | 'info' | 'error'; text: string } | null;
type Busy = 'paying' | 'authenticating' | 'checking' | null;

const POLL_MS = 3000;
const POLL_TRIES = 6;
/** „Wird noch verarbeitet": so oft von selbst nachfragen, bis der Kauf sicher
 *  entschieden ist (serverseitig spätestens nach REFILL_IN_FLIGHT_MS = 2 min). */
const RECHECK_MS = 20_000;
const RECHECK_WINDOW_MS = 5 * 60 * 1000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export default function ContactPackRefillCard() {
  const lang = useLang();
  const [status, setStatus] = useState<ContactPackRefillStatus | null>(null);
  const [packSize, setPackSize] = useState<ContactPackSize | null>(null);
  const [quote, setQuote] = useState<ContactPackRefillQuote | null>(null);
  const [quoteState, setQuoteState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [busy, setBusy] = useState<Busy>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [unresolved, setUnresolvedState] = useState<Attempt | null>(null);
  const confirmationIdRef = useRef<string>(newConfirmationId());
  const unresolvedRef = useRef<Attempt | null>(null);
  const quoteReqRef = useRef(0);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const packSizeRef = useRef<ContactPackSize | null>(null);
  const recheckTimerRef = useRef<number | null>(null);
  // Späte Bindung, damit finish() die Nachfrage planen kann, ohne im
  // useCallback-Kreis mit resolveAttempt zu hängen.
  const resolveAttemptRef = useRef<((a: Attempt) => Promise<void>) | null>(null);
  const loadQuoteRef = useRef<((size: ContactPackSize) => Promise<void>) | null>(null);

  useEffect(() => {
    packSizeRef.current = packSize;
  }, [packSize]);

  const setUnresolved = useCallback((a: Attempt | null) => {
    unresolvedRef.current = a;
    writePending(a);
    if (mountedRef.current) setUnresolvedState(a);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (recheckTimerRef.current) window.clearTimeout(recheckTimerRef.current);
    };
  }, []);

  const loadQuote = useCallback(async (size: ContactPackSize) => {
    const req = ++quoteReqRef.current;
    setQuoteState('loading');
    setQuote(null);
    // Solange ein Klick ungeklärt ist, bleibt seine Id — sonst könnte ein neuer
    // Klick mit neuer Id ein zweites Mal abbuchen.
    if (!unresolvedRef.current) confirmationIdRef.current = newConfirmationId();
    try {
      const q = await quoteContactPackRefill(size);
      // Nur die Antwort auf die LETZTE Anfrage zählt — eine späte Antwort für ein
      // anderes Paket darf nicht als dessen Betrag angezeigt werden.
      if (!mountedRef.current || req !== quoteReqRef.current || q.packSize !== size) return;
      setQuote(q);
      setQuoteState('idle');
    } catch (err) {
      if (!mountedRef.current || req !== quoteReqRef.current) return;
      if (err instanceof BillingError && err.code === 'refill_unavailable') {
        setStatus({ available: false, reason: 'card_unusable' });
        return;
      }
      setQuoteState('error');
    }
  }, []);

  const pollComplete = useCallback(async (purchaseId: string): Promise<ContactPackRefillResult> => {
    let last: ContactPackRefillResult = { status: 'processing', purchaseId };
    for (let i = 0; i < POLL_TRIES; i++) {
      await sleep(POLL_MS);
      try {
        last = await completeContactPackRefill(purchaseId);
      } catch {
        continue;
      }
      if (last.status !== 'processing') return last;
    }
    return last;
  }, []);

  /** Ein Zwischenergebnis zu Ende führen: 3DS im Browser, dann /complete;
   *  „processing" nachfragen. Die Bank-Abfrage läuft höchstens einmal. */
  const drive = useCallback(
    async (
      first: ContactPackRefillResult,
    ): Promise<{ res: ContactPackRefillResult; authShown: boolean }> => {
      let res = first;
      let authTried = false;
      let authShown = false;
      for (;;) {
        if (res.status === 'requires_action' && !authTried) {
          authTried = true;
          if (mountedRef.current) setBusy('authenticating');
          try {
            const stripe = await loadStripeCached(res.publishableKey);
            if (stripe) {
              authShown = true;
              // Kein payment_method_data o. ä. — nur das Secret (LEA-419: Extras
              // liessen 3DS hängen). Das Ergebnis liest der Server, nicht wir.
              await stripe.handleNextAction({ clientSecret: res.clientSecret });
            }
          } catch {
            /* Stripe.js nicht ladbar — der Server-Stand entscheidet unten */
          }
          res = await completeContactPackRefill(res.purchaseId);
          continue;
        }
        if (res.status === 'processing') {
          if (mountedRef.current) setBusy('checking');
          const next = await pollComplete(res.purchaseId);
          if (next.status === 'processing') return { res: next, authShown };
          res = next;
          continue;
        }
        return { res, authShown };
      }
    },
    [pollComplete],
  );

  /** Endergebnis anzeigen. `processing` bleibt ungeklärt (Id bleibt stehen). */
  const finish = useCallback(
    (res: ContactPackRefillResult, authShown: boolean) => {
      if (res.status === 'succeeded') {
        // VOR dem Mounted-Check: Header und Kontoübersicht sollen den neuen Stand
        // auch dann lesen, wenn die Karte inzwischen weg ist.
        window.dispatchEvent(new Event('cortex:credits-stale'));
      }
      if (res.status === 'processing') {
        if (!mountedRef.current) return;
        setNotice({ tone: 'info', text: t('refill.processing') });
        // Kein Sackgassen-Zustand: von selbst wieder nachfragen, solange der Kauf
        // noch nicht sicher entschieden sein kann.
        const pending = unresolvedRef.current;
        if (pending && Date.now() - pending.at < RECHECK_WINDOW_MS) {
          if (recheckTimerRef.current) window.clearTimeout(recheckTimerRef.current);
          recheckTimerRef.current = window.setTimeout(() => {
            recheckTimerRef.current = null;
            const again = unresolvedRef.current;
            if (again && mountedRef.current) void resolveAttemptRef.current?.(again);
          }, RECHECK_MS);
        }
        return;
      }
      setUnresolved(null);
      confirmationIdRef.current = newConfirmationId();
      if (!mountedRef.current) return;
      // Frisches Angebot für die NÄCHSTE Karte — das alte kann nach diesem Kauf
      // nicht mehr stimmen (z. B. verbrauchtes Kundenguthaben).
      if (packSizeRef.current) void loadQuoteRef.current?.(packSizeRef.current);
      if (res.status === 'succeeded') {
        setNotice({
          tone: 'success',
          text: t('refill.success', {
            n: formatNumber(res.contactsAdded, lang),
            balance: formatNumber(res.creditsRemaining, lang),
          }),
        });
      } else if (res.status === 'failed') {
        setNotice({ tone: 'error', text: failureText(res.code) });
      } else {
        // requires_action nach /complete. Mit gezeigter Bank-Abfrage: der Kunde
        // hat sie nicht abgeschlossen. Ohne (Stripe.js nicht ladbar): generisch.
        // Nichts abgebucht; der nächste Versuch storniert die offene Rechnung.
        setNotice({ tone: 'error', text: authShown ? t('refill.failedAuth') : t('refill.error') });
      }
    },
    [lang, setUnresolved],
  );

  /** Den Ausgang eines Klicks beim Server erfragen, ohne einen Kauf auszulösen.
   *  Ist er nie angekommen, versiegelt der Server die Id (failed/not_received). */
  const resolveAttempt = useCallback(
    async (attempt: Attempt) => {
      if (busyRef.current) return;
      busyRef.current = true;
      setBusy('checking');
      setNotice({ tone: 'info', text: t('refill.checking') });
      try {
        for (let i = 0; i < 3; i++) {
          if (i > 0) await sleep(POLL_MS);
          try {
            const r = await settleContactPackRefillAttempt(attempt.confirmationId);
            const { res, authShown } = await drive(r);
            finish(res, authShown);
            return;
          } catch {
            /* Netz/5xx — nochmal */
          }
        }
        if (mountedRef.current) setNotice({ tone: 'info', text: t('refill.unknown') });
      } finally {
        busyRef.current = false;
        if (mountedRef.current) setBusy(null);
      }
    },
    [drive, finish],
  );
  resolveAttemptRef.current = resolveAttempt;
  loadQuoteRef.current = loadQuote;

  useEffect(() => {
    let cancelled = false;
    getContactPackRefill()
      .then((s) => {
        if (cancelled) return;
        setStatus(s);
        if (!s.available) return;
        const pending = readPending();
        if (pending) {
          // Ein Klick aus einem früheren Seitenaufruf ist ungeklärt: erst den
          // Ausgang holen, bevor diese Karte wieder etwas anbietet.
          unresolvedRef.current = pending;
          confirmationIdRef.current = pending.confirmationId;
          setUnresolvedState(pending);
          setPackSize(pending.packSize);
          void resolveAttempt(pending);
          return;
        }
        const first = s.packs.find((p) => p.configured);
        if (first) setPackSize(first.contacts);
      })
      .catch(() => {
        // Nicht verfügbar / Netzfehler → die Karte bleibt unsichtbar. Der normale
        // Checkout ist davon unberührt.
        if (!cancelled) setStatus({ available: false, reason: 'disabled' });
      });
    return () => {
      cancelled = true;
    };
  }, [resolveAttempt]);

  useEffect(() => {
    if (packSize && status?.available) void loadQuote(packSize);
  }, [packSize, status?.available, loadQuote]);

  const pay = useCallback(async () => {
    if (!quote || !packSize || quote.packSize !== packSize || busyRef.current) return;
    if (unresolvedRef.current) return;
    busyRef.current = true;
    setBusy('paying');
    setNotice(null);
    const attempt: Attempt = {
      confirmationId: confirmationIdRef.current,
      packSize,
      expectedAmountCents: quote.amountDueCents,
      at: Date.now(),
      uid: currentUid(),
    };
    setUnresolved(attempt);
    let unknown = false;
    try {
      const first = await chargeContactPackRefill({
        packSize,
        confirmationId: attempt.confirmationId,
        expectedAmountCents: attempt.expectedAmountCents,
      });
      const { res, authShown } = await drive(first);
      finish(res, authShown);
    } catch (err) {
      if (err instanceof BillingError && err.code === 'amount_changed') {
        // Nichts abgebucht. Neue Karte mit dem Angebot aus der ECHTEN Rechnung
        // (Betrag UND Steuerzeile), die ein erneuter Klick bestätigt.
        setUnresolved(null);
        confirmationIdRef.current = newConfirmationId();
        if (mountedRef.current) {
          if (isQuote(err.body.quote, packSize)) setQuote(err.body.quote);
          else void loadQuote(packSize);
          setNotice({ tone: 'info', text: t('refill.amountChanged') });
        }
      } else if (err instanceof BillingError && err.code === 'refill_in_progress') {
        // Ein anderer Klick läuft (anderer Tab) oder dieser selbst noch. Dessen
        // Ausgang zeigen — dieser Klick hat keinen eigenen Kauf angelegt oder
        // IST der laufende.
        if (mountedRef.current) setNotice({ tone: 'info', text: t('refill.inProgress') });
        const purchaseId = typeof err.body.purchaseId === 'string' ? err.body.purchaseId : null;
        if (purchaseId) {
          try {
            const { res, authShown } = await drive(await pollComplete(purchaseId));
            finish(res, authShown);
          } catch {
            unknown = true;
          }
        } else {
          setUnresolved(null);
        }
      } else if (outcomeUnknown(err)) {
        unknown = true;
      } else {
        // Definitive Ablehnung vor jeder Buchung (4xx).
        setUnresolved(null);
        confirmationIdRef.current = newConfirmationId();
        if (mountedRef.current) {
          if (
            err instanceof BillingError &&
            (err.code === 'refill_unavailable' || err.code === 'customer_presence_required')
          ) {
            setStatus({ available: false, reason: 'card_unusable' });
          } else {
            setNotice({ tone: 'error', text: t('refill.error') });
          }
        }
      }
    } finally {
      busyRef.current = false;
      if (mountedRef.current) setBusy(null);
    }
    // Ausgang unbekannt: NICHT „versuch's nochmal" sagen — erst fragen, was mit
    // genau diesem Klick passiert ist.
    if (unknown) await resolveAttempt(attempt);
  }, [quote, packSize, drive, finish, loadQuote, pollComplete, resolveAttempt, setUnresolved]);

  if (!status) return null;
  if (!status.available) {
    // LEA-518: no one-click refill for this customer. A reveal customer still needs
    // a way to buy contacts, so offer the first-purchase Checkout instead of
    // rendering nothing; everyone else still sees no change.
    return isRevealCustomer() && FIRST_PURCHASE_FALLBACK.has(status.reason) ? (
      <ContactPackFirstPurchaseCard />
    ) : null;
  }

  const currency = quote?.currency ?? 'eur';
  const quoteReady = !!quote && quote.packSize === packSize && quoteState === 'idle';
  let ctaLabel: string;
  let ctaAction: () => void;
  let ctaDisabled = busy !== null;
  if (unresolved) {
    // Während nur nachgefragt wird, nicht „Zahlung läuft" anzeigen.
    ctaLabel =
      busy === 'paying' || busy === 'authenticating' ? t('refill.paying') : t('refill.checkStatus');
    ctaAction = () => void resolveAttempt(unresolved);
  } else if (quoteState === 'error') {
    ctaLabel = t('refill.retryQuote');
    ctaAction = () => {
      if (packSize) void loadQuote(packSize);
    };
  } else {
    ctaLabel = busy
      ? t('refill.paying')
      : quoteReady && quote
        ? t('refill.cta', { amount: money(quote.amountDueCents, currency, lang) })
        : t('refill.quoting');
    ctaAction = () => void pay();
    ctaDisabled = ctaDisabled || !quoteReady;
  }
  const pickerLocked = busy !== null || unresolved !== null;

  return (
    <div className="card-base overflow-hidden">
      <div className="px-6 py-4 border-b border-[#F0F4FA]">
        <div className="card-title">{t('refill.title')}</div>
        <div className="text-[12.5px] text-ink-muted leading-snug mt-0.5">
          {t('refill.subtitle')}
        </div>
      </div>
      <div className="px-6 py-5 flex flex-col gap-4">
        <fieldset className="grid grid-cols-1 sm:grid-cols-3 gap-2" disabled={pickerLocked}>
          <legend className="sr-only">{t('refill.choose')}</legend>
          {status.packs
            .filter((p) => p.configured)
            .map((p) => {
              const active = p.contacts === packSize;
              return (
                <button
                  key={p.contacts}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    if (!active && !pickerLocked) {
                      setNotice(null);
                      setPackSize(p.contacts);
                    }
                  }}
                  className={`text-left rounded-lg border px-4 py-3 transition-colors disabled:opacity-60 ${
                    active ? 'border-brand-500 bg-brand-50' : 'border-line hover:border-ink-soft'
                  }`}
                >
                  <div className="text-[13.5px] font-medium text-ink whitespace-nowrap">
                    {t('refill.pack', { n: formatNumber(p.contacts, lang) })}
                  </div>
                  <div className="text-[12px] text-ink-muted whitespace-nowrap">
                    {t('refill.net', { amount: money(p.priceCents, 'eur', lang) })}
                  </div>
                </button>
              );
            })}
        </fieldset>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[12px] text-ink-muted">{t('refill.total')}</div>
            <div
              className="text-[20px] font-semibold text-ink whitespace-nowrap"
              aria-live="polite"
            >
              {quoteReady && quote ? money(quote.amountDueCents, currency, lang) : '—'}
            </div>
            {quoteReady && quote && (
              <div className="text-[12px] text-ink-muted leading-snug">
                {quote.reverseCharge
                  ? t('refill.reverseCharge')
                  : t('refill.inclTax', { tax: money(quote.taxCents, currency, lang) })}
              </div>
            )}
            <div className="text-[12px] text-ink-muted mt-1">
              {t('refill.cardLabel', {
                brand: status.card.brand.toUpperCase(),
                last4: status.card.last4,
              })}
            </div>
          </div>
          <button
            type="button"
            onClick={ctaAction}
            disabled={ctaDisabled}
            className="btn btn-md btn-primary whitespace-nowrap w-full sm:w-auto"
          >
            {ctaLabel}
          </button>
        </div>

        <div className="text-[12px] text-ink-muted">{t('refill.terms')}</div>

        {notice && (
          <div
            role={notice.tone === 'error' ? 'alert' : 'status'}
            className={`text-[12.5px] rounded-lg px-3 py-2 ${
              notice.tone === 'success'
                ? 'bg-emerald-50 text-emerald-800'
                : notice.tone === 'error'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-slate-50 text-ink'
            }`}
          >
            {notice.text}
          </div>
        )}
      </div>
    </div>
  );
}
